import React, { useEffect, useState } from 'react';
import apiClient from '../config/api';
import Navbar from '../components/Navbar';
import { styles as layoutStyles } from '../styles/newstyles';

const emptyCategory = { name: '' };
const emptyType = { name: '', categoryId: '' };

const AttachmentManager = ({ onLogout, isAdmin, username }) => {
  const [catData, setCatData] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [catError, setCatError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedTypeId, setSelectedTypeId] = useState(null);

  const [catForm, setCatForm] = useState(emptyCategory);
  const [typeForm, setTypeForm] = useState(emptyType);
  const [catMode, setCatMode] = useState('add'); // add | edit
  const [typeMode, setTypeMode] = useState('add'); // add | edit
  const [showCatForm, setShowCatForm] = useState(false);
  const [showTypeForm, setShowTypeForm] = useState(false);

  const loadCats = async () => {
    setCatLoading(true);
    setCatError(null);
    try {
      const res = await apiClient.get('/tools/attachment-categories');
      setCatData(res.data || []);
    } catch (err) {
      console.error(err);
      setCatError(err.response?.data?.error || 'Failed to load categories.');
    } finally {
      setCatLoading(false);
    }
  };

  useEffect(() => {
    loadCats();
  }, []);

  const submitCategory = async (e) => {
    e.preventDefault();
    setSaving(true);
    setCatError(null);
    try {
      if (catMode === 'add') {
        await apiClient.post('/tools/attachment-categories', { name: catForm.name });
      } else if (catMode === 'edit' && selectedCategoryId) {
        await apiClient.put(`/tools/attachment-categories/${selectedCategoryId}`, { name: catForm.name });
      }
      setShowCatForm(false);
      setCatForm(emptyCategory);
      await loadCats();
    } catch (err) {
      console.error(err);
      setCatError(err.response?.data?.error || 'Failed to save category.');
    } finally {
      setSaving(false);
    }
  };

  const submitType = async (e) => {
    e.preventDefault();
    setSaving(true);
    setCatError(null);
    try {
      if (typeMode === 'add') {
        await apiClient.post('/tools/attachment-types', {
          name: typeForm.name,
          categoryId: parseInt(typeForm.categoryId, 10),
        });
      } else if (typeMode === 'edit' && selectedTypeId) {
        await apiClient.put(`/tools/attachment-types/${selectedTypeId}`, {
          name: typeForm.name,
          categoryId: parseInt(typeForm.categoryId, 10),
        });
      }
      setShowTypeForm(false);
      setTypeForm(emptyType);
      await loadCats();
    } catch (err) {
      console.error(err);
      setCatError(err.response?.data?.error || 'Failed to save type.');
    } finally {
      setSaving(false);
    }
  };

  // deleteCategory and deleteType removed (no longer used)

  const pageStyles = {
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 },
    title: { margin: 0, color: '#023e8a' },
    subtitle: { margin: '4px 0 0', color: '#374151' },
    actionsRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      marginTop: 12,
    },
    primaryBtn: {
      padding: '8px 12px',
      borderRadius: 10,
      border: '1px solid #1f6feb',
      background: 'linear-gradient(180deg,#1f6feb 0%,#0c4aad 100%)',
      color: '#fff',
      fontWeight: 700,
      cursor: 'pointer',
      minWidth: 130,
      flex: '0 0 auto',
    },
    secondaryBtn: {
      padding: '8px 12px',
      borderRadius: 10,
      border: '1px solid #d1d5db',
      background: '#fff',
      color: '#111827',
      fontWeight: 600,
      cursor: 'pointer',
      minWidth: 130,
      flex: '0 0 auto',
    },
    error: {
      marginTop: 12,
      padding: '10px 12px',
      borderRadius: 8,
      background: '#fef2f2',
      border: '1px solid #fecdd3',
      color: '#b91c1c',
      position: 'relative',
    },
    errorClose: {
      position: 'absolute',
      top: 8,
      right: 10,
      border: 'none',
      background: 'transparent',
      color: '#b91c1c',
      fontSize: 16,
      cursor: 'pointer',
      fontWeight: 700,
      lineHeight: 1,
    },
  };

  const formStyles = {
    wrapper: {
      marginTop: 14,
      padding: 14,
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      background: '#fff',
      boxShadow: '0 4px 10px rgba(0,0,0,0.04)',
    },
    row: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 },
    label: { width: 140, fontWeight: 600, color: '#111827' },
    input: {
      flex: 1,
      padding: '10px 12px',
      borderRadius: 10,
      border: '1px solid #d1d5db',
      fontSize: 14,
    },
    actions: { display: 'flex', gap: 10, marginTop: 6 },
    primary: {
      padding: '10px 14px',
      borderRadius: 10,
      border: '1px solid #1f6feb',
      background: 'linear-gradient(180deg,#1f6feb 0%,#0c4aad 100%)',
      color: '#fff',
      fontWeight: 700,
      cursor: 'pointer',
    },
    secondary: {
      padding: '10px 14px',
      borderRadius: 10,
      border: '1px solid #d1d5db',
      background: '#fff',
      color: '#111827',
      fontWeight: 600,
      cursor: 'pointer',
    },
  };

  const tableStyles = {
    wrap: { overflowX: 'auto', WebkitOverflowScrolling: 'touch', background: '#fff', borderRadius: 16, boxShadow: '0 8px 22px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: 720 },
    th: { textAlign: 'left', padding: '14px 12px', fontSize: 13, color: '#374151', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' },
    td: { padding: '12px 12px', fontSize: 13, color: '#111827', borderBottom: '1px solid #f1f5f9' },
    empty: { textAlign: 'center', padding: '14px 10px', color: '#6b7280' },
  };

  return (
    <div style={layoutStyles.mainContainer}>
      <Navbar username={username} onLogout={onLogout} isAdmin={isAdmin} />
      <div style={{ maxWidth: 1100, margin: '-40px auto 0', padding: '12px' }}>
        <div style={pageStyles.header}>
          <div>
            <h1 style={pageStyles.title}>Attachment Categories & Types</h1>
          </div>
        </div>

        <div style={pageStyles.actionsRow}>
          <button
            type="button"
            onClick={() => { setCatMode('add'); setCatForm(emptyCategory); setShowCatForm(true); }}
            style={pageStyles.primaryBtn}
          >
            Add Category
          </button>
          <button
            type="button"
            onClick={() => {
              if (!selectedCategoryId) return;
              const cat = catData.find(c => c.id === selectedCategoryId);
              if (!cat) return;
              setCatMode('edit');
              setCatForm({ name: cat.Name || cat.name });
              setShowCatForm(true);
            }}
            disabled={!selectedCategoryId}
            style={{ ...pageStyles.secondaryBtn, opacity: selectedCategoryId ? 1 : 0.5 }}
          >
            Edit Category
          </button>
          <button
            type="button"
            onClick={() => {
              const catId = selectedCategoryId || catData[0]?.id || '';
              setTypeMode('add');
              setTypeForm({ name: '', categoryId: catId });
              setShowTypeForm(true);
            }}
            style={pageStyles.primaryBtn}
            disabled={!catData.length}
          >
            Add Type
          </button>
          <button
            type="button"
            onClick={() => {
              if (!selectedTypeId) return;
              const cat = catData.find(c => c.types?.some(t => t.id === selectedTypeId));
              const type = cat?.types?.find(t => t.id === selectedTypeId);
              if (!type) return;
              setTypeMode('edit');
              setTypeForm({ name: type.Name || type.name, categoryId: type.categoryId || cat.id });
              setShowTypeForm(true);
            }}
            disabled={!selectedTypeId}
            style={{ ...pageStyles.secondaryBtn, opacity: selectedTypeId ? 1 : 0.5 }}
          >
            Edit Type
          </button>
        </div>

        {catError && (
          <div style={pageStyles.error}>
            <button type="button" aria-label="Dismiss" onClick={() => setCatError(null)} style={pageStyles.errorClose}>
              ×
            </button>
            {catError}
          </div>
        )}

        {showCatForm && (
          <form onSubmit={submitCategory} style={formStyles.wrapper}>
            <div style={formStyles.row}>
              <label style={formStyles.label}>Category Name</label>
              <input
                type="text"
                value={catForm.name}
                onChange={(e) => setCatForm({ name: e.target.value })}
                style={formStyles.input}
                required
              />
            </div>
            <div style={formStyles.actions}>
              <button type="submit" style={formStyles.primary} disabled={saving}>
                {saving ? 'Saving...' : catMode === 'add' ? 'Add Category' : 'Update Category'}
              </button>
              <button type="button" style={formStyles.secondary} onClick={() => setShowCatForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {showTypeForm && (
          <form onSubmit={submitType} style={formStyles.wrapper}>
            <div style={formStyles.row}>
              <label style={formStyles.label}>Type Name</label>
              <input
                type="text"
                value={typeForm.name}
                onChange={(e) => setTypeForm((f) => ({ ...f, name: e.target.value }))}
                style={formStyles.input}
                required
              />
            </div>
            <div style={formStyles.row}>
              <label style={formStyles.label}>Category</label>
              <select
                value={typeForm.categoryId}
                onChange={(e) => setTypeForm((f) => ({ ...f, categoryId: e.target.value }))}
                style={{ ...formStyles.input, height: 42 }}
                required
              >
                <option value="">Select category</option>
                {catData.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.Name || c.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={formStyles.actions}>
              <button type="submit" style={formStyles.primary} disabled={saving}>
                {saving ? 'Saving...' : typeMode === 'add' ? 'Add Type' : 'Update Type'}
              </button>
              <button type="button" style={formStyles.secondary} onClick={() => setShowTypeForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {catLoading ? (
          <div style={{ padding: 16 }}>Loading categories...</div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <div style={tableStyles.wrap}>
              <table style={tableStyles.table}>
                <thead>
                  <tr>
                    <th style={tableStyles.th} colSpan={2}>Categories</th>
                  </tr>
                </thead>
                <tbody>
                  {catData.length === 0 ? (
                    <tr>
                      <td colSpan={2} style={tableStyles.empty}>No categories yet.</td>
                    </tr>
                  ) : (
                    catData.map((cat) => (
                      <tr key={cat.id}>
                        <td style={{ ...tableStyles.td, width: 180, minWidth: 160 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                              type="radio"
                              name="selectedCategory"
                              checked={selectedCategoryId === cat.id}
                              onChange={() => { setSelectedCategoryId(cat.id); setSelectedTypeId(null); }}
                            />
                            <span>{cat.Name || cat.name}</span>
                          </div>
                        </td>
                        <td style={tableStyles.td}>
                          {cat.types?.length ? (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {cat.types.map((t) => (
                                <button
                                  key={t.id}
                                  type="button"
                                  onClick={() => { setSelectedCategoryId(cat.id); setSelectedTypeId(t.id); }}
                                  style={{
                                    padding: '6px 10px',
                                    borderRadius: 20,
                                    border: selectedTypeId === t.id ? '2px solid #1f6feb' : '1px solid #d1d5db',
                                    background: selectedTypeId === t.id ? '#e0ecff' : '#f8fafc',
                                    cursor: 'pointer',
                                  }}
                                >
                                  {t.Name || t.name}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#6b7280' }}>No types</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttachmentManager;
